import React from 'react'
import { useHistory } from 'react-router-dom'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { Button } from '@components/Basic/ButtonNbe'
import { appRoutes } from '@app/routes'
import { IoCheckmarkCircle } from 'react-icons/io5'
import { ReactComponent as Logo } from '@assets/images/logo-lb-white.svg'
import { ReactComponent as Superhero } from '@assets/images/superhero.svg'
import {
  WelcomePageWrapper,
  LeftPanel,
  RightPanel,
  LogoWrapper,
  ContentWrapper,
  HeaderSection,
  SuperheroImage,
  PlansContainer,
  PlanBox,
  PlanHeader,
  PlanContent,
  PlanTitle,
  PlanDescription,
  PlanLabel,
  FeatureList,
  FeatureNote,
  FooterSection,
} from './styled'

type PlanVariant = 'free' | 'starter' | 'pro'

const getPlanVariant = (currentPlan: string | undefined): PlanVariant => {
  if (!currentPlan) return 'free'
  const plan = currentPlan.toLowerCase()
  if (plan === 'freemium' || plan === 'free') return 'free'
  if (plan.includes('starter')) return 'starter'
  if (plan.includes('pro')) return 'pro'
  return 'free'
}

const FEATURE_INDEXES = [1, 2, 3] as const

const AFTER_TRIAL_KEYS = {
  free: {
    header: 'nbee.welcomePage.afterTrialBox.header',
    featuresLabel: 'nbee.welcomePage.afterTrialBox.featuresLabelFree',
    featureNote: 'nbee.welcomePage.afterTrialBox.featureNoteFree',
  },
  starter: {
    header: 'nbee.welcomePage.afterTrialBox.header',
    featuresLabel: 'nbee.welcomePage.afterTrialBox.featuresLabelStarter',
    featureNote: 'nbee.welcomePage.afterTrialBox.featureNoteStarter',
  },
  pro: {
    header: 'nbee.welcomePage.afterTrialBox.headerPro',
    featuresLabel: 'nbee.welcomePage.afterTrialBox.featuresLabelPro',
    featureNote: 'nbee.welcomePage.afterTrialBox.featureNotePro',
  },
} as const

export const WelcomePage: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const loginParams = queryString.parse(window.location.search)
  const variant = getPlanVariant(loginParams.currentPlan as string | undefined)

  const handleContinue = () => {
    const { bbuLink, ...otherParams } = loginParams
    const targetUrl = bbuLink
      ? `${bbuLink}?${queryString.stringify(otherParams)}`
      : `${appRoutes.nbeeStep1New.makeUrl()}?${queryString.stringify(
          otherParams
        )}`
    history.push(targetUrl)
  }

  const trialFeatures = FEATURE_INDEXES.map((i) =>
    t(`nbee.welcomePage.trialFeatures.${variant}.feature${i}`)
  )
  const afterFeatures = FEATURE_INDEXES.map((i) =>
    t(`nbee.welcomePage.afterFeatures.${variant}.feature${i}`)
  )
  const afterTrialKeys = AFTER_TRIAL_KEYS[variant]

  return (
    <WelcomePageWrapper>
      <LeftPanel>
        <LogoWrapper>
          <Logo />
        </LogoWrapper>
      </LeftPanel>

      <RightPanel>
        <ContentWrapper>
          <HeaderSection>
            <div>
              <h1>{t(`nbee.welcomePage.${variant}.title`)}</h1>
              <p>{t(`nbee.welcomePage.${variant}.subtitle`)}</p>
            </div>
            <SuperheroImage>
              <Superhero />
            </SuperheroImage>
          </HeaderSection>

          <PlansContainer>
            <PlanBox>
              <PlanHeader $variant='trial'>
                {t('nbee.welcomePage.trialBox.header')}
              </PlanHeader>
              <PlanContent>
                <PlanTitle>
                  <h3>{t(`nbee.welcomePage.${variant}.trialPlanName`)}</h3>
                </PlanTitle>
                <PlanDescription>
                  {t(`nbee.welcomePage.${variant}.trialPlanDescription`)}
                </PlanDescription>
                <PlanLabel>
                  {t('nbee.welcomePage.trialBox.featuresLabel')}
                </PlanLabel>
                <FeatureList>
                  {trialFeatures.map((feature, idx) => (
                    <li key={idx}>
                      <IoCheckmarkCircle size={18} />
                      {feature}
                    </li>
                  ))}
                </FeatureList>
                <FeatureNote>
                  {t('nbee.welcomePage.trialBox.featureNote')}
                </FeatureNote>
              </PlanContent>
            </PlanBox>

            <PlanBox>
              <PlanHeader $variant='after'>
                {t(afterTrialKeys.header)}
              </PlanHeader>
              <PlanContent>
                <PlanTitle>
                  <h3>{t(`nbee.welcomePage.${variant}.afterPlanName`)}</h3>
                </PlanTitle>
                <PlanDescription>
                  {t(`nbee.welcomePage.${variant}.afterPlanDescription`)}
                </PlanDescription>
                <PlanLabel>{t(afterTrialKeys.featuresLabel)}</PlanLabel>
                <FeatureList>
                  {afterFeatures.map((feature, idx) => (
                    <li key={idx}>
                      <IoCheckmarkCircle size={18} />
                      {feature}
                    </li>
                  ))}
                </FeatureList>
                <FeatureNote>{t(afterTrialKeys.featureNote)}</FeatureNote>
              </PlanContent>
            </PlanBox>
          </PlansContainer>

          <FooterSection>
            <Button $variant='primary' $size='large' onClick={handleContinue}>
              {t('common.continue')}
            </Button>
          </FooterSection>
        </ContentWrapper>
      </RightPanel>
    </WelcomePageWrapper>
  )
}
